import { z } from "zod";

import {
  ActivationGateSummarySchema,
  AuditPageSchema,
  BudgetStatusSchema,
  IncidentSummarySchema,
  OwnerPageEnvelopeSchema,
  OwnerRecordSchema,
  OwnerResourceSchema,
  PaginationInputSchema,
  PortfolioSummarySchema,
  RuntimeStatusSchema,
  WorkflowPageEnvelopeSchema,
  WorkflowSummarySchema,
  type ActivationGateSummary,
  type AuditPage,
  type BudgetStatus,
  type ControlResult,
  type IncidentSummary,
  type OwnerAction,
  type OwnerAuthenticationMode,
  type OwnerPage,
  type OwnerRecord,
  type OwnerResource,
  type PaginationInput,
  type PortfolioSummary,
  type RuntimeStatus,
  type WorkflowSummary
} from "./types.js";

export interface OwnerApiClient {
  readonly mode: "api" | "mock";
  readonly authenticationMode: OwnerAuthenticationMode | "mock";
  getStatus(token?: string): Promise<RuntimeStatus>;
  getBudget(organizationId: string, token?: string): Promise<BudgetStatus>;
  getAudit(organizationId: string, token?: string, afterSequence?: number): Promise<AuditPage>;
  getWorkflows(organizationId: string, token?: string, page?: PaginationInput): Promise<OwnerPage<WorkflowSummary>>;
  getPortfolio(organizationId: string, token?: string, page?: PaginationInput): Promise<OwnerPage<PortfolioSummary>>;
  getOpportunities(organizationId: string, token?: string, page?: PaginationInput): Promise<OwnerPage<OwnerRecord>>;
  getEvidence(organizationId: string, token?: string, page?: PaginationInput): Promise<OwnerPage<OwnerRecord>>;
  getProducts(organizationId: string, token?: string, page?: PaginationInput): Promise<OwnerPage<OwnerRecord>>;
  getDeployments(organizationId: string, token?: string, page?: PaginationInput): Promise<OwnerPage<OwnerRecord>>;
  getMetrics(organizationId: string, token?: string, page?: PaginationInput): Promise<OwnerPage<OwnerRecord>>;
  getCosts(organizationId: string, token?: string, page?: PaginationInput): Promise<OwnerPage<OwnerRecord>>;
  getReconciliations(organizationId: string, token?: string, page?: PaginationInput): Promise<OwnerPage<OwnerRecord>>;
  getIncidents(organizationId: string, token?: string, page?: PaginationInput): Promise<OwnerPage<IncidentSummary>>;
  getGates(organizationId: string, token?: string, page?: PaginationInput): Promise<OwnerPage<ActivationGateSummary>>;
  control(action: OwnerAction, reason: string, token?: string): Promise<ControlResult>;
}

export class OwnerApiError extends Error {
  readonly status: number;
  readonly endpoint: string | undefined;

  constructor(
    message: string,
    status: number,
    options: { endpoint?: string; cause?: unknown } = {}
  ) {
    super(message, options.cause !== undefined ? { cause: options.cause } : undefined);
    this.name = "OwnerApiError";
    this.status = status;
    this.endpoint = options.endpoint;
  }
}

export interface HttpOwnerApiClientOptions {
  readonly authenticationMode?: OwnerAuthenticationMode;
}

export class HttpOwnerApiClient implements OwnerApiClient {
  readonly mode = "api" as const;
  readonly authenticationMode: OwnerAuthenticationMode;
  readonly #baseUrl: string;
  readonly #fetch: typeof globalThis.fetch;

  constructor(
    baseUrl = "/internal/v1",
    fetchImpl?: typeof globalThis.fetch,
    options: HttpOwnerApiClientOptions = {}
  ) {
    this.#baseUrl = baseUrl.replace(/\/+$/u, "");
    this.authenticationMode = options.authenticationMode ?? "token";
    // Native fetch validates its `this`; bind it when no test/client implementation is injected.
    this.#fetch = fetchImpl ?? globalThis.fetch.bind(globalThis);
  }

  async getStatus(token = ""): Promise<RuntimeStatus> {
    return RuntimeStatusSchema.parse(await this.#request("/status", token));
  }

  async getBudget(organizationId: string, token = ""): Promise<BudgetStatus> {
    const payload = await this.#request(
      `/budget?organizationId=${encodeURIComponent(requireOrganization(organizationId))}`,
      token
    );
    return BudgetStatusSchema.parse(unwrap(payload, "budget"));
  }

  async getAudit(
    organizationId: string,
    token = "",
    afterSequence?: number
  ): Promise<AuditPage> {
    const query = new URLSearchParams({
      organizationId: requireOrganization(organizationId),
      limit: "50"
    });
    if (afterSequence !== undefined) query.set("afterSequence", String(afterSequence));
    const payload = await this.#request(`/audit?${query.toString()}`, token);
    return AuditPageSchema.parse(Array.isArray(payload) ? { events: payload } : payload);
  }

  async getWorkflows(
    organizationId: string,
    token = "",
    page: PaginationInput = {}
  ): Promise<OwnerPage<WorkflowSummary>> {
    const path = `/workflows?${pageQuery(organizationId, page)}`;
    const envelope = WorkflowPageEnvelopeSchema.parse(await this.#request(path, token));
    return pageResult(envelope.workflows, envelope.nextCursor, WorkflowSummarySchema);
  }

  async getPortfolio(organizationId: string, token = "", page: PaginationInput = {}) {
    return this.#getOwnerPage("portfolio", organizationId, token, page, PortfolioSummarySchema);
  }

  async getOpportunities(organizationId: string, token = "", page: PaginationInput = {}) {
    return this.#getOwnerPage("opportunities", organizationId, token, page, OwnerRecordSchema);
  }

  async getEvidence(organizationId: string, token = "", page: PaginationInput = {}) {
    return this.#getOwnerPage("evidence", organizationId, token, page, OwnerRecordSchema);
  }

  async getProducts(organizationId: string, token = "", page: PaginationInput = {}) {
    return this.#getOwnerPage("products", organizationId, token, page, OwnerRecordSchema);
  }

  async getDeployments(organizationId: string, token = "", page: PaginationInput = {}) {
    return this.#getOwnerPage("deployments", organizationId, token, page, OwnerRecordSchema);
  }

  async getMetrics(organizationId: string, token = "", page: PaginationInput = {}) {
    return this.#getOwnerPage("metrics", organizationId, token, page, OwnerRecordSchema);
  }

  async getCosts(organizationId: string, token = "", page: PaginationInput = {}) {
    return this.#getOwnerPage("costs", organizationId, token, page, OwnerRecordSchema);
  }

  async getReconciliations(organizationId: string, token = "", page: PaginationInput = {}) {
    return this.#getOwnerPage("reconciliations", organizationId, token, page, OwnerRecordSchema);
  }

  async getIncidents(organizationId: string, token = "", page: PaginationInput = {}) {
    return this.#getOwnerPage("incidents", organizationId, token, page, IncidentSummarySchema);
  }

  async getGates(organizationId: string, token = "", page: PaginationInput = {}) {
    return this.#getOwnerPage("gates", organizationId, token, page, ActivationGateSummarySchema);
  }

  async control(action: OwnerAction, reason: string, token = ""): Promise<ControlResult> {
    const payload = await this.#request(`/owner/${action}`, token, {
      method: "POST",
      headers: { "Idempotency-Key": crypto.randomUUID() },
      body: JSON.stringify({ reason })
    });
    const accepted =
      typeof payload === "object" && payload !== null && "accepted" in payload
        ? Boolean(payload.accepted)
        : true;
    return {
      accepted,
      simulated: false,
      action,
      message: accepted ? `${action} accepted by control API` : `${action} was not accepted`
    };
  }

  async #getOwnerPage<T>(
    rawResource: OwnerResource,
    organizationId: string,
    token: string,
    page: PaginationInput,
    schema: z.ZodType<T>
  ): Promise<OwnerPage<T>> {
    const resource = OwnerResourceSchema.parse(rawResource);
    const path = `/owner/${resource}?${pageQuery(organizationId, page)}`;
    const envelope = OwnerPageEnvelopeSchema.parse(await this.#request(path, token));
    if (envelope.resource !== resource) {
      throw new OwnerApiError(
        `Control API ${path} returned resource ${envelope.resource} instead of ${resource}`,
        502,
        { endpoint: path }
      );
    }
    return pageResult(envelope.items, envelope.nextCursor, schema);
  }

  async #request(path: string, token: string, init: RequestInit = {}): Promise<unknown> {
    const emergencyToken = token.trim();
    if (this.authenticationMode === "token" && !emergencyToken) {
      throw new OwnerApiError("Owner token is required", 401, { endpoint: path });
    }
    const headers = new Headers(init.headers);
    if (emergencyToken) headers.set("authorization", `Bearer ${emergencyToken}`);
    headers.set("accept", "application/json");
    if (init.body !== undefined) headers.set("content-type", "application/json");

    let response: Response;
    try {
      response = await this.#fetch(`${this.#baseUrl}${path}`, {
        ...init,
        cache: "no-store",
        credentials: this.authenticationMode === "access" ? "include" : "omit",
        headers
      });
    } catch (cause) {
      const detail = cause instanceof Error ? cause.message : String(cause);
      throw new OwnerApiError(
        `Network request to ${path} failed (${detail}). The API origin may be unreachable or blocked by the console CSP/CORS.`,
        0,
        { endpoint: path, cause }
      );
    }

    if (!response.ok) {
      const body = (await response.text().catch(() => "")).trim();
      const suffix = body ? `: ${body.slice(0, 300)}` : "";
      throw new OwnerApiError(
        `Control API ${path} failed with HTTP ${response.status}${suffix}`,
        response.status,
        { endpoint: path }
      );
    }
    return response.json() as Promise<unknown>;
  }
}

export class MockOwnerApiClient implements OwnerApiClient {
  readonly mode = "mock" as const;
  readonly authenticationMode = "mock" as const;
  #state: RuntimeStatus = {
    organizationId: "mock-organization",
    executionMode: "shadow",
    state: "running",
    activeWorkflows: 2,
    pendingActions: 1,
    openIncidents: 1,
    updatedAt: "2026-07-11T12:00:00.000Z"
  };

  async getStatus(): Promise<RuntimeStatus> {
    return this.#state;
  }

  async getBudget(organizationId: string): Promise<BudgetStatus> {
    return {
      organizationId,
      currency: "EUR",
      monthlyLimit: 500,
      committed: 74,
      spent: 82.5,
      remaining: 343.5,
      periodStart: "2026-07-01T00:00:00.000Z",
      periodEnd: "2026-08-01T00:00:00.000Z"
    };
  }

  async getAudit(): Promise<AuditPage> {
    return {
      events: [
        {
          id: "mock-event-2",
          sequence: 2,
          occurredAt: "2026-07-11T12:00:00.000Z",
          eventType: "workflow.intent.created",
          outcome: "shadow",
          actor: "DiscoverWorkflow",
          summary: "Market signal collection intent created",
          correlationId: "mock-correlation"
        },
        {
          id: "mock-event-1",
          sequence: 1,
          occurredAt: "2026-07-11T11:59:00.000Z",
          eventType: "runtime.started",
          outcome: "shadow",
          actor: "control-plane",
          summary: "Safe shadow runtime started"
        }
      ],
      nextSequence: 2
    };
  }

  async getWorkflows(_organizationId: string, _token = "", page: PaginationInput = {}) {
    return paginateMock([
      {
        id: "mock-workflow-1",
        workflowName: "DiscoverWorkflow",
        kind: "discover",
        status: "running",
        currentStepIndex: 1,
        totalSteps: 3,
        updatedAt: "2026-07-11T12:00:00.000Z"
      },
      {
        id: "mock-workflow-2",
        workflowName: "OperateWorkflow",
        kind: "operate",
        status: "paused",
        currentStepIndex: 2,
        totalSteps: 3,
        updatedAt: "2026-07-11T11:58:00.000Z"
      },
      {
        id: "mock-workflow-3",
        workflowName: "BuildWorkflow",
        kind: "build",
        status: "completed",
        currentStepIndex: 4,
        totalSteps: 4,
        updatedAt: "2026-07-10T17:00:00.000Z"
      }
    ].map((item) => WorkflowSummarySchema.parse(item)), page);
  }

  async getPortfolio(_organizationId: string, _token = "", page: PaginationInput = {}) {
    return paginateMock([PortfolioSummarySchema.parse({
      products: 1,
      opportunities: 7,
      activeWorkflows: 2,
      pendingActions: 1,
      openIncidents: 1
    })], page);
  }

  async getOpportunities(_organizationId: string, _token = "", page: PaginationInput = {}) {
    return paginateMock([OwnerRecordSchema.parse({ id: "mock-opportunity", status: "qualified" })], page);
  }

  async getEvidence(_organizationId: string, _token = "", page: PaginationInput = {}) {
    return paginateMock([OwnerRecordSchema.parse({ id: "mock-evidence", sourceType: "public_web" })], page);
  }

  async getProducts(_organizationId: string, _token = "", page: PaginationInput = {}) {
    return paginateMock([OwnerRecordSchema.parse({ id: "mock-product", status: "preview" })], page);
  }

  async getDeployments(_organizationId: string, _token = "", page: PaginationInput = {}) {
    return paginateMock([OwnerRecordSchema.parse({ id: "mock-deployment", status: "healthy" })], page);
  }

  async getMetrics(_organizationId: string, _token = "", page: PaginationInput = {}) {
    return paginateMock([OwnerRecordSchema.parse({ id: "mock-metric", key: "activation", value: 0.42 })], page);
  }

  async getCosts(_organizationId: string, _token = "", page: PaginationInput = {}) {
    return paginateMock([OwnerRecordSchema.parse({ id: "mock-cost", amountMinor: 125 })], page);
  }

  async getReconciliations(_organizationId: string, _token = "", page: PaginationInput = {}) {
    return paginateMock([OwnerRecordSchema.parse({ id: "mock-reconciliation", state: "matched" })], page);
  }

  async getIncidents(_organizationId: string, _token = "", page: PaginationInput = {}) {
    return paginateMock([IncidentSummarySchema.parse({
      id: "mock-incident",
      severity: "medium",
      status: "mitigating",
      summary: "Synthetic readiness probe delay",
      detectedAt: "2026-07-11T11:57:00.000Z",
      requiresHuman: false
    })], page);
  }

  async getGates(_organizationId: string, _token = "", page: PaginationInput = {}) {
    return paginateMock([
      ActivationGateSummarySchema.parse({ ordinal: 1, name: "Human bootstrap", status: "active", requiredChecks: ["accounts", "MFA"] }),
      ActivationGateSummarySchema.parse({ ordinal: 2, name: "Durable shadow", status: "active", requiredChecks: ["PostgreSQL", "OPA", "Restate"] }),
      ActivationGateSummarySchema.parse({ ordinal: 3, name: "Real shadow", status: "locked", requiredChecks: ["7-day observation"] })
    ], page);
  }

  async control(action: OwnerAction): Promise<ControlResult> {
    this.#state = {
      ...this.#state,
      state: action === "kill" ? "killed" : action === "pause" ? "paused" : "running",
      updatedAt: new Date().toISOString()
    };
    return {
      accepted: true,
      simulated: true,
      action,
      message: `${action} simulated locally; no network request was made`
    };
  }
}

export function createOwnerApiClient(environment: {
  readonly mode?: string | undefined;
  readonly baseUrl?: string | undefined;
  readonly authenticationMode?: string | undefined;
}): OwnerApiClient {
  if (environment.mode !== "api") return new MockOwnerApiClient();
  return new HttpOwnerApiClient(environment.baseUrl || "/internal/v1", undefined, {
    authenticationMode: environment.authenticationMode === "token" ? "token" : "access"
  });
}

function pageQuery(organizationId: string, input: PaginationInput): string {
  const page = PaginationInputSchema.parse(input);
  const query = new URLSearchParams({
    organizationId: requireOrganization(organizationId),
    limit: String(page.limit)
  });
  if (page.cursor !== undefined) query.set("cursor", page.cursor);
  return query.toString();
}

function pageResult<T>(
  rawItems: readonly unknown[],
  nextCursor: string | undefined,
  schema: z.ZodType<T>
): OwnerPage<T> {
  const items = rawItems.map((item) => schema.parse(item));
  return {
    items,
    ...(nextCursor === undefined ? {} : { nextCursor })
  };
}

function paginateMock<T>(items: readonly T[], input: PaginationInput): OwnerPage<T> {
  const page = PaginationInputSchema.parse(input);
  const offsetMatch = page.cursor?.match(/^mock_(\d+)$/u);
  const offset = offsetMatch ? Number(offsetMatch[1]) : 0;
  const end = Math.min(items.length, offset + page.limit);
  return {
    items: items.slice(offset, end),
    ...(end < items.length ? { nextCursor: `mock_${end}` } : {})
  };
}

function requireOrganization(value: string): string {
  const organizationId = value.trim();
  if (!organizationId) throw new OwnerApiError("Organization is required", 400);
  return organizationId;
}

function unwrap(payload: unknown, key: string): unknown {
  if (typeof payload === "object" && payload !== null && key in payload) {
    return (payload as Record<string, unknown>)[key];
  }
  return payload;
}
